import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ownerAccountAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';

/** An owner viewing and editing their own account. */
const OwnerProfileModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!show) return;
    setFailure(null);
    setDone(false);
    setLoading(true);

    ownerAccountAPI
      .getProfile()
      .then(({ data }) => {
        setProfile(data);
        setForm({
          fullName: data.fullName || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          currentPassword: '',
          newPassword: '',
        });
      })
      .catch((err) => setFailure(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [show]);

  const set = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFailure(null);
    setDone(false);

    try {
      await ownerAccountAPI.updateProfile({
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        currentPassword: form.currentPassword || null,
        newPassword: form.newPassword || null,
      });
      setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '' }));
      setDone(true);
    } catch (err) {
      setFailure(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (name, labelKey, icon, type = 'text') => (
    <div className="ad-modal-field">
      <label className="ad-modal-label">{t(`owner.profile.${labelKey}`)}</label>
      <span className="ad-input-wrap">
        <i className={`fas ${icon} ad-input-icon`}></i>
        <input
          type={type}
          className="ad-input-sm ad-input-has-icon"
          placeholder={t(`owner.profile.${labelKey}`)}
          value={form[name]}
          onChange={set(name)}
        />
      </span>
    </div>
  );

  return (
    <Modal
      show={show}
      title={t('owner.profile.title')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ad-btn" onClick={onClose} disabled={saving}>
            {t('common.close')}
          </button>
          <button
            type="submit"
            form="owner-profile-form"
            className="ad-btn ad-btn-primary"
            disabled={saving || loading}
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </>
      }
    >
      <form id="owner-profile-form" onSubmit={submit}>
        {failure && <p className="ad-error-text">{failure}</p>}
        {done && <p className="ad-profile-done">{t('owner.profile.saved')}</p>}

        {profile && (
          <div className="ad-ledger-head">
            <span>
              {t('owner.profile.identityNumber')}: <strong>{profile.identityNumber}</strong>
            </span>
            <span>
              {t('owner.profile.properties')}: <strong>{profile.propertiesCount}</strong>
            </span>
            {profile.bankName && (
              <span>
                {t('owner.profile.bankName')}: <strong>{profile.bankName}</strong>
              </span>
            )}
          </div>
        )}

        <div className="ad-modal-grid">
          {field('fullName', 'fullName', 'fa-user')}
          {field('phoneNumber', 'phoneNumber', 'fa-mobile-screen')}
          {field('email', 'email', 'fa-envelope', 'email')}
        </div>

        <h4 className="ad-intake-subheading">{t('owner.profile.changePassword')}</h4>
        <div className="ad-modal-grid">
          {field('currentPassword', 'currentPassword', 'fa-lock', 'password')}
          {field('newPassword', 'newPassword', 'fa-key', 'password')}
        </div>
        <p className="ad-hint">{t('owner.profile.passwordHint')}</p>
      </form>
    </Modal>
  );
};

export default OwnerProfileModal;
